import { makeFieldSchema } from '@backstage/plugin-scaffolder-react';
import { TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useEffect, useState } from 'react';
import { ScaffolderField } from '@backstage/plugin-scaffolder-react/alpha';
import { useConfigurationItems } from './useConfigurationItems';

export const ConfigurationItemPickerFieldSchema = makeFieldSchema({
  output: z =>
    z.object({
      appId: z.string().describe('The AppId of the selected configuration item'),
      name: z.string().describe('The configuration item name'),
    }),
});

type ConfigurationItemPickerProps = typeof ConfigurationItemPickerFieldSchema.TProps;

export type ConfigurationItem = { name: string; appId: string };

export const ConfigurationItemPicker = (props: ConfigurationItemPickerProps) => {
  const MINIMUM_LENGTH_FOR_SEARCH = 1;
  const {
    schema: {
      title = 'Configuration item',
      description = 'Configuration items from Business Applications Inventory. Start typing to search.',
    },
    onChange,
    rawErrors,
    required,
    formData,
    errors,
  } = props;
  const getNameFromForm = (form: any): string => form?.name ?? '';
  const getCIFromForm = (form: any) => (form ? (form as ConfigurationItem) : null);

  const [searchText, setSearchText] = useState(getNameFromForm(formData));
  const [ci, setCI] = useState<ConfigurationItem | null>(getCIFromForm(formData));
  const { configurationItems, isLoading } = useConfigurationItems(
    searchText,
    MINIMUM_LENGTH_FOR_SEARCH,
  );

  // Update internal state based on formData from scaffolder
  useEffect(() => {
    setSearchText(getNameFromForm(formData));
    setCI(getCIFromForm(formData));
  }, [formData]);

  // Update scaffolder data (same as formData) when ci changes
  useEffect(() => {
    const isValid = ci?.appId && ci?.name;
    onChange(isValid ? ci : undefined);
  }, [ci]); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearchTextChange = (_: any, newValue: string) => {
    setSearchText(newValue);
    setCI(null);
  };

  const onSelectionChange = (_: any, newValue: string | ConfigurationItem | null) => {
    if (typeof newValue !== 'string') {
      setCI(newValue);
    }
  };

  return (
    <ScaffolderField
      rawErrors={rawErrors}
      rawDescription={description}
      required={required}
      errors={errors}
    >
      <Autocomplete
        inputValue={searchText}
        onInputChange={onSearchTextChange}
        options={configurationItems || []}
        getOptionLabel={_ci => _ci?.name}
        onChange={onSelectionChange}
        loading={isLoading}
        renderInput={params => <TextField {...params} required={required} label={title} />}
        freeSolo
        selectOnFocus
        handleHomeEndKeys
        disableClearable
      />
    </ScaffolderField>
  );
};
